/**
 * Atlas Tea Co. — Touch-device mega menu adapter
 * File: assets/atlas-touch-nav.js
 *
 * Problem
 * -------
 * Minimog's desktop mega menu (SiteNav class) relies on mouseenter /
 * mouseleave. On touch devices wide enough to show the desktop header
 * (e.g. iPad landscape ≥ 1024px), tapping a mega menu parent link
 * navigates immediately instead of opening the panel.
 *
 * Solution
 * --------
 * On coarse-pointer devices we directly toggle the same classes
 * SiteNav uses (m-menu__item--active, m-mega-active, slide-reveal)
 * rather than dispatching synthetic mouse events. This avoids the
 * browser's touch → mouseenter → mouseleave emulation sequence
 * which fires a mouseleave after the click and tears the panel
 * down immediately.
 *
 * We also suppress SiteNav's own mouseenter / mouseleave handlers
 * by capturing those events on mega menu items and stopping them.
 * This prevents the browser's emulated mouse events from fighting
 * with our touch-managed state.
 *
 * Non-mega menu items (.m-menu__item without .m-menu__item--mega)
 * are completely unaffected — taps go straight to the href.
 *
 * Loading
 * -------
 * Add to sections/header.liquid (or layout/theme.liquid), after
 * Minimog's own mega-menu.js has loaded:
 *
 *   <script src="{{ 'atlas-touch-nav.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // -----------------------------------------------------------------
  // Gate: only activate on devices whose primary pointer is coarse.
  // -----------------------------------------------------------------
  if (!window.matchMedia('(pointer: coarse)').matches) return;

  var MEGA_ITEM_SEL = '.m-header__desktop .m-menu__item--mega';
  var ACTIVE_CLASS  = 'm-menu__item--active';
  var MEGA_ACTIVE   = 'm-mega-active';
  var SLIDE_REVEAL  = 'slide-reveal';
  var SLIDE_RIGHT   = 'slide-from-right';

  // -----------------------------------------------------------------
  // Block SiteNav's mouseenter / mouseleave on mega menu items.
  //
  // On touch devices the browser synthesises these around every tap.
  // SiteNav binds them to open/close the panel on desktop. By
  // stopping propagation in the capture phase we prevent SiteNav's
  // handlers from firing, so only our click-based logic controls
  // the panel state.
  // -----------------------------------------------------------------
  document.querySelectorAll(MEGA_ITEM_SEL).forEach(function (item) {
    item.addEventListener('mouseenter', function (e) {
      e.stopPropagation();
    }, true);

    item.addEventListener('mouseleave', function (e) {
      e.stopPropagation();
    }, true);
  });

  // -----------------------------------------------------------------
  // Helpers: directly toggle the classes SiteNav uses.
  // -----------------------------------------------------------------

  function getHeader(menuItem) {
    return menuItem.closest('header') || menuItem.closest('m-header');
  }

  function openMegaMenu(menuItem) {
    var header  = getHeader(menuItem);
    var subMenu = menuItem.querySelector('.m-mega-menu');

    // Set panel dimensions (SiteNav does this via getElementBoundingRect)
    if (subMenu && header) {
      var rect = subMenu.getBoundingClientRect();
      header.style.setProperty('--sf-dropdown-width', rect.width);
      header.style.setProperty('--sf-dropdown-height', rect.height);
    }

    // Add the same classes SiteNav.onMenuItemEnter adds
    if (header) {
      header.classList.add(SLIDE_REVEAL);
      header.classList.add(MEGA_ACTIVE);
    }
    menuItem.classList.add(ACTIVE_CLASS);
  }

  function closeMegaMenu(menuItem) {
    var header = getHeader(menuItem);

    menuItem.classList.remove(ACTIVE_CLASS);

    if (header) {
      header.classList.remove(MEGA_ACTIVE, SLIDE_REVEAL, SLIDE_RIGHT);
    }
  }

  function closeAllMegaMenus() {
    var openItems = document.querySelectorAll(
      MEGA_ITEM_SEL + '.' + ACTIVE_CLASS
    );
    for (var i = 0; i < openItems.length; i++) {
      closeMegaMenu(openItems[i]);
    }
  }

  // -----------------------------------------------------------------
  // Single delegated click listener on document.
  //
  //  • Tap on a mega menu parent link:
  //      – Panel closed → prevent navigation, open panel.
  //      – Panel open   → let the tap navigate (second tap).
  //
  //  • Tap inside a mega menu panel (.m-mega-menu):
  //      – Do nothing, let the link navigate normally.
  //
  //  • Tap anywhere else:
  //      – Close any open mega menus.
  // -----------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var link = e.target.closest(MEGA_ITEM_SEL + ' > .m-menu__link');

    if (!link) {
      // Not a parent link tap.
      if (!e.target.closest('.m-mega-menu')) {
        closeAllMegaMenus();
      }
      return;
    }

    var menuItem = link.closest('.m-menu__item--mega');

    if (menuItem.classList.contains(ACTIVE_CLASS)) {
      // Panel is already open — second tap navigates.
      return;
    }

    // First tap — open the mega menu instead of navigating.
    e.preventDefault();
    closeAllMegaMenus();
    openMegaMenu(menuItem);
  });
})();